/**
 * CandidateDetailPage (Plan 03-07 / HR1-04/05/06/07/08 + OPS-02) — full review
 * surface at /admin/candidates/:id. Five sections: personal data + consent stamp +
 * returning-candidate priors (D6) · score + per-competency bars + fairness flags
 * (HR1-08) · MC answers · short-answer responses · tab-switch timeline (D4). Action
 * footer is canMutate-gated (advance/reject/unreject — hr_recruiter cannot unreject,
 * HR1-06). T-1 pattern: a 404 redirects to /positions with a toast.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/lib/auth";
import { canMutate } from "@/lib/permissions";
import { mapErrorSlug } from "@/lib/error-slug-mapper";
import {
  useCandidateDetailQuery,
  useAdvanceMutation,
  useRejectMutation,
  useUnrejectMutation,
} from "./hooks";
import type { CandidateAnswerDetail } from "./api";
import { TabSwitchTimelineChart } from "./TabSwitchTimelineChart";
import { Stage2InviteCopyButton } from "./Stage2InviteCopyButton";
import { Stage2DetailSection } from "./Stage2DetailSection";

function mmss(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fullName(d: {
  first_name: string | null;
  primer_apellido: string | null;
  segundo_apellido: string | null;
  email: string | null;
}): string {
  const parts = [d.first_name, d.primer_apellido, d.segundo_apellido].filter(
    Boolean,
  );
  return parts.length ? parts.join(" ") : (d.email ?? "—");
}

function AnswerList({
  answers,
  emptyLabel,
}: {
  answers: CandidateAnswerDetail[];
  emptyLabel: string;
}) {
  const { t } = useTranslation("admin-candidates");
  if (answers.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="space-y-2">
      {answers.map((a) => {
        const text = (a.question_snapshot?.text as string) ?? `#${a.position_question_id}`;
        return (
          <details
            key={a.position_question_id}
            className="rounded-md border border-input px-3 py-2"
          >
            <summary className="cursor-pointer text-sm font-medium">
              {text}
            </summary>
            <div className="mt-2 space-y-1 text-sm">
              {a.question_type === "multiple_choice" ? (
                <p>
                  <span className="text-muted-foreground">{a.choice_id ?? "—"}</span>{" "}
                  {a.is_correct === true ? (
                    <Badge style={{ backgroundColor: "#16a34a", color: "#fff" }}>
                      {t("detail.correct")}
                    </Badge>
                  ) : a.is_correct === false ? (
                    <Badge style={{ backgroundColor: "#dc2626", color: "#fff" }}>
                      {t("detail.incorrect")}
                    </Badge>
                  ) : null}
                </p>
              ) : (
                <>
                  <p className="whitespace-pre-wrap">{a.short_answer_text ?? "—"}</p>
                  <Badge variant="secondary">{t("detail.reviewer_required")}</Badge>
                </>
              )}
              <p className="text-muted-foreground">
                {t("detail.total_time")}: {mmss(a.elapsed_seconds)}
              </p>
            </div>
          </details>
        );
      })}
    </div>
  );
}

export function CandidateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const candidateId = Number(id);
  const { t } = useTranslation("admin-candidates");
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user)?.role;

  const query = useCandidateDetailQuery(candidateId);
  const advance = useAdvanceMutation(candidateId);
  const reject = useRejectMutation(candidateId);
  const unreject = useUnrejectMutation(candidateId);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // T-1 redirect on 404 / load failure (Wave A pattern).
  useEffect(() => {
    if (query.isError) {
      toast.error(mapErrorSlug(query.error));
      navigate("/positions", { replace: true });
    }
  }, [query.isError, query.error, navigate]);

  if (query.isLoading) {
    return (
      <div className="px-4 py-12 text-center text-muted-foreground">
        {t("detail.loading")}
      </div>
    );
  }
  if (!query.data) return null;
  const d = query.data;

  async function runAction(
    fn: () => Promise<unknown>,
    after?: () => void,
    successMsg?: string,
  ): Promise<void> {
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      after?.();
    } catch (err) {
      toast.error(mapErrorSlug(err));
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{fullName(d)}</h1>
          <p className="text-sm text-muted-foreground">
            {d.reference_number ?? "—"} ·{" "}
            <Badge variant="outline">{t(`status.${d.stage_1_status}`)}</Badge>
          </p>
        </div>
        <Link
          to={`/positions/${d.position_id}`}
          className="text-sm underline min-h-[44px] inline-flex items-center"
        >
          {t("actions.back_to_position")}
        </Link>
      </div>

      {/* Section 1 — personal data */}
      <Card>
        <CardContent className="py-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <h2 className="col-span-full font-semibold">{t("detail.personal_data")}</h2>
          <div><span className="text-muted-foreground">{t("detail.phone")}: </span>{d.phone ?? "—"}</div>
          <div>
            <span className="text-muted-foreground">{t("detail.salary")}: </span>
            {d.salary_expectation != null ? `${d.salary_expectation.toLocaleString()} ${d.salary_currency ?? ""}` : "—"}
          </div>
          <div><span className="text-muted-foreground">{t("detail.earliest_start")}: </span>{d.earliest_start_date ?? "—"}</div>
          <div><span className="text-muted-foreground">{t("detail.consent_stamp")}: </span>{new Date(d.consent_accepted_at).toLocaleString()}</div>
          <div><span className="text-muted-foreground">{t("detail.consent_version")}: </span>#{d.consent_version_id}</div>
          <div><span className="text-muted-foreground">{t("detail.consent_ip")}: </span>{d.consent_ip_address}</div>
        </CardContent>
      </Card>

      {/* Section 1b — returning candidate */}
      {d.prior_applications_count > 0 ? (
        <Card>
          <CardContent className="py-4 text-sm">
            <h2 className="font-semibold">
              {t("detail.prior_applications")}{" "}
              <Badge variant="secondary">×{d.prior_applications_count}</Badge>
            </h2>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              {d.viewer_scoped_prior_applications.map((p) => (
                <li key={p.position_id}>
                  {p.position_title} — {t(`status.${p.stage_1_status}`)}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {/* Section 2 — score + competency bars + fairness */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <h2 className="font-semibold">{t("detail.score_title")}</h2>
          <p className="text-2xl font-semibold tabular-nums">
            {d.stage_1_score != null ? d.stage_1_score.toFixed(1) : "—"}
          </p>
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t("detail.competency_scores")}</h3>
            {Object.entries(d.per_competency_scores).map(([comp, score]) => {
              const pct = score <= 1 ? score * 100 : score;
              return (
                <div key={comp} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>{comp}</span>
                    <span className="tabular-nums">{Math.round(pct)}%</span>
                  </div>
                  <Progress value={Math.round(pct)} />
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <span className="text-sm text-muted-foreground">
              {t("detail.total_time")}: {mmss(d.total_elapsed_seconds)}
            </span>
            {d.fairness_flags.too_fast ? (
              <Badge style={{ backgroundColor: "#dc2626", color: "#fff" }}>{t("flags.too_fast")}</Badge>
            ) : d.fairness_flags.too_slow ? (
              <Badge style={{ backgroundColor: "#d97706", color: "#fff" }}>{t("flags.too_slow")}</Badge>
            ) : (
              <Badge style={{ backgroundColor: "#16a34a", color: "#fff" }}>{t("flags.in_band")}</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section 3 — MC answers */}
      <section className="space-y-2">
        <h2 className="font-semibold">{t("detail.mc_answers")}</h2>
        <AnswerList answers={d.mc_answers} emptyLabel={t("detail.no_answers")} />
      </section>

      {/* Section 4 — short answers */}
      <section className="space-y-2">
        <h2 className="font-semibold">{t("detail.sa_answers")}</h2>
        <AnswerList answers={d.short_answer_responses} emptyLabel={t("detail.no_answers")} />
      </section>

      {/* Section 5 — tab-switch timeline */}
      <section className="space-y-2">
        <h2 className="font-semibold">
          {t("flags.tab_switch_timeline")}{" "}
          <Badge variant="secondary">{d.tab_switch_count}</Badge>
        </h2>
        <TabSwitchTimelineChart events={d.tab_switch_timeline} />
      </section>

      {/* Section 6 — Stage-2 decision support (renders only when stage >= stage2_done) */}
      <Stage2DetailSection d={d} />

      {/* Actions footer */}
      <div className="flex flex-wrap gap-3 border-t pt-4">
        {canMutate("candidate", "advance", role) ? (
          <Button
            onClick={() =>
              runAction(
                () => advance.mutateAsync(undefined),
                undefined,
                t("actions.advance_success"),
              )
            }
            disabled={advance.isPending}
            className="min-h-[44px]"
            data-testid="advance-btn"
          >
            {t("actions.advance")}
          </Button>
        ) : null}
        {canMutate("candidate", "reject", role) ? (
          <Button
            onClick={() => setRejectOpen(true)}
            className="min-h-[44px]"
            style={{ backgroundColor: "#dc2626", color: "#fff" }}
            data-testid="reject-btn"
          >
            {t("actions.reject")}
          </Button>
        ) : null}
        {canMutate("candidate", "unreject", role) ? (
          <Button
            variant="outline"
            onClick={() =>
              runAction(
                () => unreject.mutateAsync(),
                undefined,
                t("actions.unreject_success"),
              )
            }
            disabled={unreject.isPending}
            className="min-h-[44px]"
            data-testid="unreject-btn"
          >
            {t("actions.unreject")}
          </Button>
        ) : null}
        <Stage2InviteCopyButton
          candidateId={candidateId}
          positionLanguage={"es"}
        />
      </div>

      {/* Reject reason dialog */}
      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title={t("actions.reject")}
      >
        <div className="my-4 space-y-2">
          <label htmlFor="reject-reason" className="text-sm">
            {t("actions.reject_reason_label")}
          </label>
          <Textarea
            id="reject-reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="min-h-[80px]"
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setRejectOpen(false)} className="min-h-[44px]">
            {t("actions.cancel")}
          </Button>
          <Button
            disabled={reject.isPending}
            className="min-h-[44px]"
            style={{ backgroundColor: "#dc2626", color: "#fff" }}
            onClick={() =>
              runAction(
                () => reject.mutateAsync(rejectReason || undefined),
                () => {
                  setRejectOpen(false);
                  setRejectReason("");
                },
                t("actions.reject_success"),
              )
            }
          >
            {t("actions.confirm")}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
